import { useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router";
import { sanitizeAnalyticsUrl, sanitizeReferrer } from "../lib/analytics-url";

declare global {
  interface Window {
    posthog?: {
      identify: (userId: string, properties?: Record<string, any>) => void;
      capture: (eventName: string, properties?: Record<string, any>) => void;
      reset: () => void;
      people?: {
        set: (properties: Record<string, any>) => void;
      };
    };
  }
}

type AnalyticsOptions = {
  trackPageView?: boolean;
  trackClicks?: boolean;
  trackScroll?: boolean;
};

// デバイスタイプを判定
function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

// 流入元を判定
function getSource(): string {
  const params = new URLSearchParams(window.location.search);
  if (params.get("fbclid")) return "meta";
  if (params.get("gclid")) return "google";

  const utmSource = params.get("utm_source")?.toLowerCase();
  if (utmSource) {
    if (utmSource.includes("facebook") || utmSource.includes("instagram") || utmSource.includes("meta")) return "meta";
    if (utmSource.includes("google")) return "google";
    if (utmSource.includes("yahoo")) return "yahoo";
    if (utmSource.includes("line")) return "line";
    return utmSource;
  }

  if (document.referrer) {
    try {
      const referrerHost = new URL(document.referrer).hostname;
      if (referrerHost.includes("google")) return "organic_google";
      if (referrerHost.includes("yahoo")) return "organic_yahoo";
      if (referrerHost.includes("bing")) return "organic_bing";
      return "referral";
    } catch {
      return "referral";
    }
  }
  return "direct";
}

export function useAnalytics(options: AnalyticsOptions = {}) {
  const {
    trackPageView = true,
    trackClicks = true,
    trackScroll = true,
  } = options;

  const location = useLocation();
  const maxScrollDepthRef = useRef(0);

  // ページビュートラッキング
  useEffect(() => {
    if (!trackPageView || !window.posthog) return;

    window.posthog.capture("$pageview", {
      $current_url: sanitizeAnalyticsUrl(window.location.href),
      $pathname: location.pathname,
      source: getSource(),
      deviceType: getDeviceType(),
      referrer: sanitizeReferrer(document.referrer),
    });
  }, [location.pathname, trackPageView]);

  // クリックトラッキング
  const trackClick = useCallback((eventName: string, properties?: Record<string, any>) => {
    if (!window.posthog) return;

    window.posthog.capture(eventName, {
      page: location.pathname,
      deviceType: getDeviceType(),
      ...properties,
    });
  }, [location.pathname]);

  // ヘッダーナビゲーションクリック
  const trackHeaderNavClick = useCallback((label: string, href: string) => {
    trackClick("hp_header_nav_click", {
      label,
      href,
      isMobile: getDeviceType() === "mobile",
    });
  }, [trackClick]);

  // キャンペーンバナークリック
  const trackCampaignBannerView = useCallback(() => {
    if (!window.posthog) return;

    window.posthog.capture("hp_campaign_banner_view", {
      page: location.pathname,
      deviceType: getDeviceType(),
    });
  }, [location.pathname]);

  // モバイルメニューの開閉
  const trackMobileMenu = useCallback((action: "open" | "close") => {
    trackClick("hp_mobile_menu_toggle", {
      action,
    });
  }, [trackClick]);

  // CTA（Call to Action）クリック
  const trackCTAClick = useCallback((ctaType: string, ctaLocation?: string) => {
    trackClick("hp_cta_click", {
      ctaType,
      ctaLocation,
      source: getSource(),
    });
  }, [trackClick]);

  // スクロール深度トラッキング
  useEffect(() => {
    if (!trackScroll || !window.posthog) return;

    maxScrollDepthRef.current = 0;
    const scrollMilestones = new Set<number>();

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);

      if (scrollPercent > maxScrollDepthRef.current) {
        maxScrollDepthRef.current = scrollPercent;
      }

      const milestones = [25, 50, 75, 100];
      for (const milestone of milestones) {
        if (scrollPercent >= milestone && !scrollMilestones.has(milestone)) {
          scrollMilestones.add(milestone);

          window.posthog?.capture(`hp_scroll_${milestone}`, {
            page: location.pathname,
            deviceType: getDeviceType(),
          });
        }
      }
    };

    let scrollTimer: NodeJS.Timeout;
    const debouncedHandleScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(handleScroll, 100);
    };

    window.addEventListener("scroll", debouncedHandleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", debouncedHandleScroll);
      clearTimeout(scrollTimer);
    };
  }, [location.pathname, trackScroll]);

  // フォーム分析用
  const trackFormInteraction = useCallback((formName: string, fieldName: string, action: "focus" | "blur" | "change") => {
    trackClick("hp_form_interaction", {
      formName,
      fieldName,
      action,
    });
  }, [trackClick]);

  const trackFormSubmit = useCallback((formName: string, success: boolean, errorMessage?: string) => {
    trackClick("hp_form_submit", {
      formName,
      success,
      errorMessage,
    });
  }, [trackClick]);

  // 動画トラッキング
  const trackVideoEvent = useCallback((videoId: string, eventType: "play" | "pause" | "complete" | "progress", progress?: number) => {
    trackClick(`hp_video_${eventType}`, {
      videoId,
      progress,
    });
  }, [trackClick]);

  // エラートラッキング
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (!window.posthog) return;

      window.posthog.capture("hp_javascript_error", {
        page: location.pathname,
        error: event.message,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
      });
    };

    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, [location.pathname]);

  // セッション時間トラッキング
  useEffect(() => {
    const startTime = Date.now();

    const handleBeforeUnload = () => {
      const sessionDuration = Math.floor((Date.now() - startTime) / 1000);

      if (window.posthog) {
        // PostHog SDKに委譲し、設定済みのリージョンとCSPを維持する。
        // capture_pageleave が有効なため、SDK側でBeacon送信される。
        window.posthog.capture("hp_page_exit", {
          page: location.pathname,
          sessionDuration,
          maxScrollDepth: maxScrollDepthRef.current,
          deviceType: getDeviceType(),
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handleBeforeUnload);
    };
  }, [location.pathname]);

  return {
    trackClick,
    trackHeaderNavClick,
    trackCampaignBannerView,
    trackMobileMenu,
    trackCTAClick,
    trackFormInteraction,
    trackFormSubmit,
    trackVideoEvent,
  };
}
